import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'antd-mobile';

import useLineLiff from 'hooks/LineLiff';
import request from 'pages/toyotaPremiumOfficialAccount/utils/request';

const REGION_ORDER = ['北部', '中部', '南部', '東部'];
const CITY_ORDER = [
  '台北市',
  '新北市',
  '桃園市',
  '台中市',
  '台南市',
  '高雄市',
  '基隆市',
  '新竹市',
  '嘉義市',
  '新竹縣',
  '苗栗縣',
  '彰化縣',
  '南投縣',
  '雲林縣',
  '嘉義縣',
  '屏東縣',
  '宜蘭縣',
  '花蓮縣',
  '台東縣',
  '澎湖縣',
  '金門縣',
  '連江縣',
];
const SKELETON_CITY_WIDTHS = ['72px', '56px', '64px', '80px', '48px', '68px'];
class CityHelper {
  static #normalizeCity(city) {
    return String(city ?? '')
      .trim()
      .replaceAll('臺', '台');
  }

  static cityRank(city) {
    const index = CITY_ORDER.indexOf(this.#normalizeCity(city));
    return index === -1 ? CITY_ORDER.length : index;
  }

  static compareCities(left, right) {
    const rankDiff = this.cityRank(left) - this.cityRank(right);
    if (rankDiff !== 0) return rankDiff;
    return this.#normalizeCity(left).localeCompare(
      this.#normalizeCity(right),
      'zh-Hant'
    );
  }
}

const getConversationStatus = async () => {
  const response = await request.get('/conversations');

  return (
    response.data.data || {
      conversation: null,
      sales_offices: [],
    }
  );
};
const createConversation = async ({ salesOfficeId }) => {
  const response = await request.post('/conversations', {
    id: salesOfficeId,
  });

  return response.data;
};

const groupSalesOffices = (offices) => {
  const grouped = {};

  for (const office of offices) {
    if (!grouped[office.region]) grouped[office.region] = [];

    let cityGroup = grouped[office.region].find(
      (item) => item.city === office.city
    );
    if (!cityGroup) {
      cityGroup = { city: office.city, branches: [] };
      grouped[office.region].push(cityGroup);
    }

    cityGroup.branches.push({
      id: office.id,
      name: office.name,
      mapUrl: office.map_url,
    });
  }

  for (const region of Object.keys(grouped)) {
    grouped[region].sort((left, right) =>
      CityHelper.compareCities(left.city, right.city)
    );
  }

  const extraRegions = Object.keys(grouped).filter(
    (region) => !REGION_ORDER.includes(region)
  );
  const orderedRegions = [
    ...REGION_ORDER.filter((region) => grouped[region]),
    ...extraRegions,
  ];

  return Object.fromEntries(
    orderedRegions.map((region) => [region, grouped[region]])
  );
};

function BranchListSkeleton() {
  return (
    <div
      className="branch-selector__loading"
      role="status"
      aria-live="polite"
      aria-busy="true">
      <p className="branch-selector__sr-only">營業所載入中</p>
      <div className="branch-selector__tabs" aria-hidden="true">
        {REGION_ORDER.map((region) => (
          <div className="branch-selector__tab" key={region}>
            <span className="branch-selector__skeleton-label" />
            <span className="branch-selector__tab-indicator" />
          </div>
        ))}
      </div>
      <div className="branch-selector__cities" aria-hidden="true">
        {SKELETON_CITY_WIDTHS.map((width, index) => (
          <div className="branch-selector__select" key={index}>
            <div className="branch-selector__select-trigger branch-selector__select-trigger--skeleton">
              <span
                className="branch-selector__skeleton-bar"
                style={{ width }}
              />
              <span className="branch-selector__skeleton-icon" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BranchList() {
  const navigate = useNavigate();
  const [activeRegion, setActiveRegion] = useState(null);
  const [openCity, setOpenCity] = useState(null);
  const [regions, setRegions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const isCreatingRef = useRef(false);
  const currentCities = regions[activeRegion] || [];

  const { userInfo } = useLineLiff(process.env.REACT_APP_LIFF_ID_CONSULTATION);

  const handleCityClick = (city) => {
    setOpenCity((prev) => (prev === city ? null : city));
  };
  const handleRegionChange = (region) => {
    setActiveRegion(region);
    setOpenCity(regions[region][0].city);
  };
  const handleBranchClick = async (branch) => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    const loading = Toast.show({
      content: '配對中，請稍候',
      icon: 'loading',
      duration: 0,
      maskClickable: false,
    });
    try {
      const response = await createConversation({ salesOfficeId: branch.id });
      loading.close();
      navigate(
        `/ToyotaPremium/consultation/user-chatroom/${response.data.id}`,
        { state: { showHello: true } }
      );
    } catch {
      // request.js already replaced this toast with the error toast; closing
      // it here would hide that message (antd-mobile Toast is a singleton).
      isCreatingRef.current = false;
    }
  };

  useEffect(() => {
    if (!userInfo) return;
    const fetchOptions = async () => {
      try {
        const { conversation, sales_offices } = await getConversationStatus();
        if (conversation && conversation.status === 'active') {
          navigate(
            `/ToyotaPremium/consultation/user-chatroom/${conversation.id}`
          );
          return;
        }
        const grouped = groupSalesOffices(sales_offices || []);
        const firstRegion = Object.keys(grouped)[0];
        setRegions(grouped);
        setActiveRegion(firstRegion || null);
        setOpenCity(firstRegion ? grouped[firstRegion][0]?.city : null);
      } catch {
        // toast already shown in request.js
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, [navigate, userInfo]);

  return (
    <div className="branch-selector" aria-busy={isLoading}>
      <div className="branch-selector__header">
        <p className="branch-selector__title">請選擇營業所</p>
        <p className="branch-selector__description">
          系統將依地區為您配對最近的 TOYOTA PREMIUM 業代。
        </p>
      </div>
      {isLoading ? (
        <BranchListSkeleton />
      ) : !Object.keys(regions).length ? (
        <p className="branch-selector__empty">目前沒有可選擇的營業所</p>
      ) : (
        <>
          <div className="branch-selector__tabs">
            {Object.keys(regions).map((region) => (
              <button
                key={region}
                className={`branch-selector__tab ${
                  activeRegion === region ? 'branch-selector__tab--active' : ''
                }`}
                type="button"
                onClick={() => handleRegionChange(region)}>
                <span>{region}</span>
                <span className="branch-selector__tab-indicator"></span>
              </button>
            ))}
          </div>

          <div className="branch-selector__cities">
            {currentCities.map((city) => (
              <div className="branch-selector__select" key={city.city}>
                <button
                  className="branch-selector__select-trigger"
                  onClick={() => handleCityClick(city.city)}
                  type="button">
                  <span className="branch-selector__select-trigger-text">
                    {city.city}
                  </span>

                  <svg
                    className={`branch-selector__select-trigger-icon ${
                      openCity === city.city
                        ? 'branch-selector__select-trigger-icon--open'
                        : ''
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none">
                    <path
                      d="M3.19922 5.73324L7.86589 10.3999L12.5326 5.73324"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {openCity === city.city && (
                  <div className="branch-selector__select-options">
                    {city.branches.map((branch) => (
                      <button
                        className="branch-selector__select-option"
                        key={branch.id}
                        type="button"
                        onClick={() => handleBranchClick(branch)}>
                        <span className="branch-selector__select-option-name">
                          {branch.name}
                        </span>
                        <a
                          className="branch-selector__select-option-location"
                          href={branch.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}>
                          <svg
                            className="branch-selector__select-option-location-icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M10 1C11.8565 1 13.6365 1.73803 14.9492 3.05078C16.262 4.36354 17 6.14348 17 8C17 12.3684 12.1544 16.9179 10.5264 18.3236C10.3748 18.4376 10.1897 18.5 10 18.5C9.8103 18.5 9.62525 18.4376 9.47363 18.3236C7.84562 16.9179 3 12.3684 3 8C3 6.14348 3.73803 4.36354 5.05078 3.05078C6.36354 1.73803 8.14348 1 10 1ZM10 4.85C8.2603 4.85 6.85 6.2603 6.85 8C6.85 9.7397 8.2603 11.15 10 11.15C11.7397 11.15 13.15 9.7397 13.15 8C13.15 6.2603 11.7397 4.85 10 4.85Z"
                              fill="currentColor"
                            />
                          </svg>
                          <span className="branch-selector__select-option-location-text">
                            查看位置
                          </span>
                        </a>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default BranchList;
